import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, gradientForId, Progress, Spinner, StatusBadge, Text, type StatusBadgeProps } from "@athena/ui";
import { cn } from "@athena/ui/utils";
import { AlertCircle, Check, ChevronDown, Film, RotateCw, X } from "lucide-react";
import { formatFileSize } from "../../../../global/formatHelpers";
import { isUploaded, type QueueItem } from "./uploadQueue";

type StepState = "pending" | "active" | "done" | "failed";

/**
 * The three things each video goes through, as the row shows them. A failure is pinned on the step
 * it happened in: a transfer that never finished leaves `progress` short of 1, a transcode failure
 * does not.
 */
const stepStates = (item: QueueItem): { upload: StepState; process: StepState; details: StepState } => {
    const dead = item.phase === "failed" || item.phase === "interrupted";
    const failedInTransfer = dead && item.progress < 1;
    return {
        upload: failedInTransfer ? "failed" : item.phase === "queued" || item.phase === "uploading" ? "active" : "done",
        process: dead
            ? failedInTransfer
                ? "pending"
                : "failed"
            : item.phase === "ready"
              ? "done"
              : item.phase === "processing"
                ? "active"
                : "pending",
        details: item.published ? "done" : item.publishing || item.finishWhenUploaded ? "active" : "pending",
    };
};

type BadgeVariant = NonNullable<StatusBadgeProps["variant"]>;

/** The single headline status, in the order an operator would want to know it. */
const useStatus = (item: QueueItem): { label: string; variant: BadgeVariant } => {
    const { t } = useTranslation();
    const percent = Math.round(item.progress * 100);
    if (item.phase === "interrupted") return { label: t("admin.bulkUpload.statusInterrupted"), variant: "destructive" };
    if (item.phase === "failed") return { label: t("admin.bulkUpload.statusFailed"), variant: "destructive" };
    if (isUploaded(item)) return { label: t("admin.bulkUpload.statusUploaded"), variant: "success" };
    if (item.publishing) return { label: t("admin.bulkUpload.statusSaving"), variant: "sky" };
    if (item.published) return { label: t("admin.bulkUpload.statusDetailsSaved"), variant: "sky" };
    if (item.finishWhenUploaded) return { label: t("admin.bulkUpload.statusFinishQueued"), variant: "sky" };
    if (item.phase === "queued") return { label: t("admin.bulkUpload.statusQueued"), variant: "muted" };
    if (item.phase === "uploading")
        return { label: t("admin.bulkUpload.statusUploading", { percent }), variant: "brand" };
    return { label: t("admin.bulkUpload.statusNeedsDetails"), variant: "amber" };
};

const StepDot = ({ state, label }: { state: StepState; label: string }) => (
    <span
        className={cn(
            "inline-flex items-center gap-1 text-xs",
            state === "done" && "text-success",
            state === "active" && "text-primary",
            state === "failed" && "text-destructive",
            state === "pending" && "text-muted-foreground",
        )}
    >
        <span
            aria-hidden
            className={cn(
                "flex h-4 w-4 items-center justify-center rounded-full border",
                state === "done" && "border-success bg-success text-success-foreground",
                state === "active" && "border-primary",
                state === "failed" && "border-destructive bg-destructive text-destructive-foreground",
                state === "pending" && "border-border",
            )}
        >
            {state === "done" && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
            {state === "active" && <span className="bg-primary h-1.5 w-1.5 animate-pulse rounded-full" />}
            {state === "failed" && <X className="h-2.5 w-2.5" strokeWidth={3} />}
        </span>
        {label}
    </span>
);

interface UploadQueueRowProps {
    item: QueueItem;
    /** Open: the row expands to show `children`, the video's details form. */
    selected: boolean;
    /** Opens this row, or closes it when it is already open. */
    onToggle: () => void;
    onRetry: () => void;
    /** Cancel, discard or clear — the page decides which, and whether to confirm first. */
    onRemove: () => void;
    /** What the row expands to show while selected. */
    children?: ReactNode;
}

export function UploadQueueRow({ item, selected, onToggle, onRetry, onRemove, children }: UploadQueueRowProps) {
    const { t } = useTranslation();
    const rowRef = useRef<HTMLLIElement>(null);

    /*
     * Bring a newly opened row to the top of the list — "Finish & next" can open one below the fold.
     * `start` rather than `nearest`: the row above closes in the same render, and `nearest` then
     * settled with this row's header scrolled off the top, leaving a form with no video above it.
     */
    useEffect(() => {
        if (selected) rowRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, [selected]);
    const status = useStatus(item);
    const steps = stepStates(item);
    const title = item.details.title.trim() || item.filename;
    const working = item.phase === "uploading" || item.phase === "processing";
    const removeLabel =
        item.phase === "uploading"
            ? t("admin.bulkUpload.cancelUpload")
            : item.assetId && !item.published
              ? t("admin.bulkUpload.discardUpload")
              : t("admin.bulkUpload.removeFromQueue");

    return (
        <li
            ref={rowRef}
            className={cn(
                "group bg-card relative flex scroll-my-2 flex-col rounded-xl border transition-all duration-150",
                selected ? "border-primary ring-primary/20 shadow-sm ring-2" : "border-border hover:border-primary/40",
            )}
        >
            <div className="flex items-center gap-3 p-3">
                {/* The row's main area opens it; the action buttons sit outside so they are not nested in it. */}
                <button
                    type="button"
                    onClick={onToggle}
                    aria-expanded={selected}
                    aria-label={title}
                    className="focus-visible:ring-ring flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left focus-visible:ring-2 focus-visible:outline-none"
                >
                    <span
                        className={cn(
                            "relative flex aspect-video w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-linear-to-br",
                            !item.posterUrl && gradientForId(item.key),
                        )}
                    >
                        {item.posterUrl ? (
                            <img src={item.posterUrl} alt="" className="h-full w-full object-cover" />
                        ) : (
                            <Film className="h-5 w-5 text-white/80" />
                        )}
                        {working && (
                            <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                                <Spinner className="h-4 w-4 text-white" />
                            </span>
                        )}
                        {isUploaded(item) && (
                            <span className="bg-success text-success-foreground absolute right-1 bottom-1 rounded-full p-0.5">
                                <Check className="h-3 w-3" strokeWidth={3} />
                            </span>
                        )}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="flex items-center gap-2">
                            <Text as="span" weight="medium" size="sm" className="truncate">
                                {title}
                            </Text>
                        </span>
                        <Text as="span" size="xs" muted className="truncate">
                            {item.filename}
                            {item.sizeBytes > 0 && ` · ${formatFileSize(item.sizeBytes)}`}
                        </Text>
                        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <StepDot state={steps.upload} label={t("admin.bulkUpload.stepUpload")} />
                            <StepDot state={steps.process} label={t("admin.bulkUpload.stepProcess")} />
                            <StepDot state={steps.details} label={t("admin.bulkUpload.stepDetails")} />
                        </span>
                        {item.phase === "uploading" && (
                            <Progress
                                value={Math.round(item.progress * 100)}
                                className="mt-1 h-1.5"
                                aria-label={t("admin.bulkUpload.statusUploading", {
                                    percent: Math.round(item.progress * 100),
                                })}
                            />
                        )}
                        {item.error && (
                            <Text as="span" size="xs" className="text-destructive flex items-start gap-1">
                                <AlertCircle className="mt-px h-3 w-3 shrink-0" />
                                <span className="line-clamp-2">{item.error}</span>
                            </Text>
                        )}
                    </span>
                </button>
                {/* One line on the right: status, actions, then the disclosure arrow at the very edge. */}
                <span className="flex shrink-0 items-center gap-1">
                    <StatusBadge variant={status.variant} size="xs" dot className="mr-1">
                        {status.label}
                    </StatusBadge>
                    {item.phase === "failed" && item.file && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={onRetry}
                            aria-label={`${t("admin.bulkUpload.retry")} ${item.filename}`}
                            title={t("admin.bulkUpload.retry")}
                        >
                            <RotateCw className="h-3.5 w-3.5" />
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive h-7 w-7"
                        onClick={onRemove}
                        disabled={item.publishing}
                        aria-label={`${removeLabel} ${item.filename}`}
                        title={removeLabel}
                    >
                        <X className="h-3.5 w-3.5" />
                    </Button>
                    {/*
                     * A second way to the row's own toggle, so it is kept out of the tab order and the
                     * accessibility tree — the row button already announces expanded or collapsed.
                     */}
                    <Button
                        variant="ghost"
                        size="icon"
                        tabIndex={-1}
                        aria-hidden
                        className="text-muted-foreground h-7 w-7"
                        onClick={onToggle}
                    >
                        <ChevronDown
                            className={cn("h-4 w-4 transition-transform duration-200", selected && "rotate-180")}
                        />
                    </Button>
                </span>
            </div>
            {selected && children && <div className="border-border border-t p-4">{children}</div>}
        </li>
    );
}
